import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { useAuthContext } from 'context/authContext';
import useBranchPermission from './useBranchPermission';
import {
  getCaseStudies,
  createCaseStudy,
  updateCaseStudy,
  deleteCaseStudy
} from 'lib/supabaseQueries';

// orphanId optional: the beneficiary tab passes it, the WeConnect page does not
export function useCaseStudies(orphanId) {
  const { organization, isInitialized } = useAuthContext();
  const { branchId, isMainBranch } = useBranchPermission();
  const effectiveBranchId = isMainBranch ? null : branchId;

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['caseStudies', organization?.id, effectiveBranchId, orphanId],
    queryFn: () => getCaseStudies(organization.id, effectiveBranchId, orphanId),
    enabled: isInitialized && !!organization?.id
  });

  return { caseStudies: data?.data || [], isLoading, refetch };
}

export function useSaveCaseStudy() {
  const queryClient = useQueryClient();
  const { organization, user } = useAuthContext();

  const mutation = useMutation({
    mutationFn: async ({
      id,
      orphanId,
      branchId,
      title,
      lifeBefore,
      howFoundOut,
      experience,
      lifeNow,
      quote,
      studyDate,
      photos
    }) => {
      const payload = {
        title,
        life_before: lifeBefore || null,
        how_found_out: howFoundOut || null,
        experience: experience || null,
        life_now: lifeNow || null,
        quote: quote || null,
        study_date: studyDate,
        photos: photos || []
      };
      const result = id
        ? await updateCaseStudy(id, payload)
        : await createCaseStudy({
            ...payload,
            orphan_id: orphanId,
            organisation_id: organization.id,
            branch_id: branchId || null,
            created_by: user?.id || null
          });
      if (result.error) throw new Error(result.error);
      return result;
    },
    onSuccess: (_r, variables) => {
      queryClient.invalidateQueries({ queryKey: ['caseStudies'] });
      toast.success(variables.id ? 'Case study updated' : 'Case study added');
    },
    onError: error => {
      console.error('Save case study error:', error);
      toast.error(error.message || 'Failed to save case study');
    }
  });

  return {
    saveCaseStudy: mutation.mutate,
    saveCaseStudyAsync: mutation.mutateAsync,
    isSaving: mutation.isPending
  };
}

export function useDeleteCaseStudy() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async id => {
      const result = await deleteCaseStudy(id);
      if (result.error) throw new Error(result.error);
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['caseStudies'] });
      toast.success('Case study deleted');
    },
    onError: () => toast.error('Failed to delete case study')
  });
  return { deleteCaseStudy: mutation.mutate, isDeleting: mutation.isPending };
}
